import { useAuth } from '@/contexts/AuthContext';
import { switchableRoles } from '@/config/navigation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AppRole, defaultDashboardFor } from '@/config/roles';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

interface RoleSwitcherProps {
  variant?: 'sidebar' | 'dropdown';
  className?: string;
  triggerClassName?: string;
}

export const RoleSwitcher = ({
  variant = 'sidebar',
  className = '',
  triggerClassName = '',
}: RoleSwitcherProps) => {
  const { roles, activeRole, switchRole } = useAuth();
  const navigate = useNavigate();

  // Filter out restricted roles (super_admin and admin never see fan option)
  const userSwitchableRoles = roles.filter(role => {
    // Super admin never sees fan option
    if (roles.includes('super_admin' as AppRole) && role === 'fan') {
      return false;
    }
    
    // Admin never sees fan option
    if (roles.includes('admin' as AppRole) && role === 'fan') {
      return false;
    }
    
    return switchableRoles.includes(role);
  });

  if (userSwitchableRoles.length <= 1) {
    return null;
  }

  const formatRoleName = (role: AppRole) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const handleRoleSwitch = async (newRole: string) => {
    const roleAsAppRole = newRole as AppRole;
    
    // Update last_used_at
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase
        .from('user_roles')
        .update({ last_used_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('role', roleAsAppRole);
    }
    
    switchRole(roleAsAppRole);
    
    // Navigate to dashboard and refresh
    // let rolePath = roleAsAppRole === 'super_admin' ? 'super' : roleAsAppRole;
    // if (roleAsAppRole === 'tournament_organizer') {
    //   rolePath = 'organizer';
    // }
    // navigate(`/${rolePath}/dashboard`);
    // window.location.reload();
    navigate(defaultDashboardFor(roles, roleAsAppRole));
  };

  const wrapperClassName = variant === 'dropdown' ? 'w-auto' : 'px-3 py-2';
  const triggerWidthClass = variant === 'dropdown' ? 'w-[140px] h-8 text-xs' : 'w-full';

  return (
    <div className={`${wrapperClassName} ${className}`.trim()}>
      {variant === 'sidebar' && (
        <div className="text-xs font-medium text-muted-foreground mb-1">
          Current Role
        </div>
      )}
      <Select value={activeRole || undefined} onValueChange={handleRoleSwitch}>
        <SelectTrigger className={`${triggerWidthClass} ${triggerClassName}`.trim()}>
          <SelectValue placeholder="Select role" />
        </SelectTrigger>
        <SelectContent>
          {userSwitchableRoles.map(role => (
            <SelectItem key={role} value={role}>
              <div className="flex items-center justify-between w-full">
                <span>{formatRoleName(role)}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
